import {
    AlertOutlined,
    FileExclamationOutlined,
    DisconnectOutlined,
    StopOutlined,
    CloudOutlined,
    QuestionCircleOutlined,
} from "@ant-design/icons";
import tr from "../i18n";

export const NO_WATERING_CAUSES = {
    OFFLINE: "offline",
    WEATHER_OPTIMIZATION: "weather_optimization",
    VALVES_DISABLED: "valves_disabled",
    NO_LOG: "no_log",
    NO_FLOW: "no_flow",
    UNKNOWN: "unknown",
};

/**
 * `order` is DISPLAY order (what needs an operator's attention first), which is
 * deliberately not the backend's resolution precedence. A rain cancel is the
 * least alarming outcome even though it outranks no_flow when deciding the cause.
 *
 * Labels/hints are functions so tr() runs at render time - the existing
 * TYPE_CONFIG calls tr() at module scope, which freezes the locale at import.
 */
const CAUSE_CONFIG = {
    [NO_WATERING_CAUSES.NO_FLOW]: {
        order: 1,
        color: "#ff4d4f",
        soft: "#fff1f0",
        border: "#ffccc7",
        icon: AlertOutlined,
        label: () => tr("No water flow"),
        hint: () => tr("The device reported a log, but no water passed. Check the meter, valves and line."),
    },
    [NO_WATERING_CAUSES.NO_LOG]: {
        order: 2,
        color: "#fa541c",
        soft: "#fff2e8",
        border: "#ffbb96",
        icon: FileExclamationOutlined,
        label: () => tr("No irrigation report"),
        hint: () => tr("The device was reachable but sent no irrigation log for its plan."),
    },
    [NO_WATERING_CAUSES.OFFLINE]: {
        order: 3,
        color: "#8c8c8c",
        soft: "#fafafa",
        border: "#e8e8e8",
        icon: DisconnectOutlined,
        label: () => tr("Offline"),
        hint: () => tr("The device has not connected in the last 24 hours."),
    },
    [NO_WATERING_CAUSES.VALVES_DISABLED]: {
        order: 4,
        color: "#fa8c16",
        soft: "#fff7e6",
        border: "#ffd591",
        icon: StopOutlined,
        label: () => tr("All valves off"),
        hint: () => tr("Every valve on the device is switched off, so no plan can run."),
    },
    [NO_WATERING_CAUSES.WEATHER_OPTIMIZATION]: {
        order: 5,
        color: "#1677ff",
        soft: "#f0f7ff",
        border: "#bae0ff",
        icon: CloudOutlined,
        label: () => tr("Canceled by weather"),
        hint: () => tr("Rain or wind optimization canceled this watering. No action needed."),
    },
    [NO_WATERING_CAUSES.UNKNOWN]: {
        order: 6,
        color: "#bfbfbf",
        soft: "#fafafa",
        border: "#e8e8e8",
        icon: QuestionCircleOutlined,
        label: () => tr("Cause not determined"),
        hint: () => tr("This device did not water, but no specific cause was recorded."),
    },
};

export const getCauseConfig = (cause) =>
    CAUSE_CONFIG[cause] || CAUSE_CONFIG[NO_WATERING_CAUSES.UNKNOWN];

const WEATHER_LABELS = {
    rain: () => tr("Rain"),
    wind: () => tr("Wind"),
    sun: () => tr("Sun"),
};

/**
 * Turns the backend `meta` blob into one short line for the chip tooltip.
 * Returns null when there is nothing worth saying - the caller then shows only
 * the cause hint rather than an empty row.
 */
export function formatCauseDetail(cause, meta, formatDate) {
    if (!meta) return null;
    const fmt = formatDate || ((d) => String(d));

    switch (cause) {
        case NO_WATERING_CAUSES.OFFLINE:
            return meta.lastAliveAt
                ? `${tr("Last seen")}: ${fmt(meta.lastAliveAt)}`
                : tr("Never connected");

        case NO_WATERING_CAUSES.WEATHER_OPTIMIZATION: {
            const kinds = (meta.optimizationTypes || [])
                .map((t) => (WEATHER_LABELS[t] ? WEATHER_LABELS[t]() : t))
                .join(", ");
            const minutes = (meta.canceledPlans || [])
                .reduce((sum, p) => sum + (p.expectedMinutes || 0), 0);
            const parts = [];
            if (kinds) parts.push(kinds);
            if (minutes) parts.push(`${minutes} ${tr("min canceled")}`);
            return parts.length ? parts.join(" · ") : null;
        }

        case NO_WATERING_CAUSES.VALVES_DISABLED:
            return meta.totalValves
                ? `${meta.disabledValves}/${meta.totalValves} ${tr("valves off")}`
                : null;

        case NO_WATERING_CAUSES.NO_LOG:
            return meta.expectedMinutes
                ? `${tr("Expected")} ${meta.expectedMinutes} ${tr("min")} · ${tr("no log received")}`
                : null;

        case NO_WATERING_CAUSES.NO_FLOW: {
            const parts = [];
            if (meta.expectedMinutes) parts.push(`${tr("Expected")} ${meta.expectedMinutes} ${tr("min")}`);
            parts.push(meta.hasMeter
                ? `${tr("measured flow")} ${meta.measuredFlow ?? 0}`
                : `${tr("reported")} ${meta.reportedMinutes ?? 0} ${tr("min")}`);
            return parts.join(" · ");
        }

        default:
            return null;
    }
}

/**
 * Normalizes the /properties response into ONE list of devices that each carry
 * a cause, tolerating three server states so the UI can ship independently of
 * the backend deploy:
 *   1. new endpoint  - devices already carry `cause`/`meta`
 *   2. old endpoint  - merge cause in by deviceSerial from the notification doc
 *   3. fully legacy  - devices/offlineDevices buckets become unknown/offline
 */
export function resolveNotificationDevices(props, notification) {
    const taggedBySerial = new Map();
    for (const d of notification?.nonWateringDevices || []) {
        if (d?.deviceSerial) taggedBySerial.set(d.deviceSerial, d);
    }

    const decorate = (device, fallbackCause) => {
        const tagged = device?.deviceSerial ? taggedBySerial.get(device.deviceSerial) : null;
        return {
            ...device,
            cause: device?.cause || tagged?.cause || fallbackCause,
            meta: device?.meta || tagged?.meta || null,
        };
    };

    const offline = (props?.offlineDevices || [])
        .map((d) => decorate(d, NO_WATERING_CAUSES.OFFLINE));
    const rest = (props?.devices || [])
        .map((d) => decorate(d, NO_WATERING_CAUSES.UNKNOWN));

    // A serial can appear in both buckets while the backend dual-writes.
    const seen = new Set();
    return [...offline, ...rest].filter((d) => {
        const key = d.deviceSerial || d._id || d.id;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/** [{ cause, config, items }] sorted by display order. */
export function groupDevicesByCause(devices) {
    const byCause = new Map();
    for (const d of devices) {
        const cause = d.cause || NO_WATERING_CAUSES.UNKNOWN;
        if (!byCause.has(cause)) byCause.set(cause, []);
        byCause.get(cause).push(d);
    }
    return [...byCause.entries()]
        .map(([cause, items]) => ({ cause, items, config: getCauseConfig(cause) }))
        .sort((a, b) => a.config.order - b.config.order);
}

/** Counts straight off the notification doc - no extra request needed. */
export function summarizeCauses(nonWateringDevices) {
    if (!nonWateringDevices?.length) return [];
    const counts = new Map();
    for (const d of nonWateringDevices) {
        const cause = d.cause || NO_WATERING_CAUSES.UNKNOWN;
        counts.set(cause, (counts.get(cause) || 0) + 1);
    }
    return [...counts.entries()]
        .map(([cause, count]) => ({ cause, count, config: getCauseConfig(cause) }))
        .sort((a, b) => a.config.order - b.config.order);
}